"use client";

import React, { useEffect, useRef, useState } from "react";
import styles from "./MMTab.module.scss";
import { mmTabData } from "./mmTab.data";
import Button from "@components/atoms/Button";
import Image from "@components/atoms/Image";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import assetLoader from "@utils/internalImageLoder";

const STICKY_HEADER_HEIGHT = 135;

interface Tab {
  id: string;
  label: string;
}

interface MMTabProps {
  showExpertTab?: boolean;
  brandName?: string;
  modelName?: string;
  cityName?: string;
  showComparisonTab?: any;
  carAvailability?: string;
  pageType?: string;
  isSellable?: boolean;
}

const MMTab = ({
  showExpertTab = true,
  brandName,
  modelName,
  cityName,
  showComparisonTab = true,
  carAvailability = "Yes",
  pageType = "MM page",
  isSellable,
}: MMTabProps) => {
  let filteredTabs = mmTabData;

  if (!showExpertTab) {
    filteredTabs = filteredTabs.filter((tab) => tab.id !== "expert-review");
  }

  if (!showComparisonTab) {
    filteredTabs = filteredTabs.filter((tab) => tab.id !== "comparison");
  }

  if (!isSellable) {
    filteredTabs = filteredTabs.filter((tab) => tab.id !== "chat");
  }
  const getInitialTab = () => {
    const expertTab = filteredTabs.find((tab) => tab.id === "expert-review");
    return expertTab ? expertTab.id : null;
  };

  const [activeTab, setActiveTab] = useState<string | null>(getInitialTab());
  const isClickScroll = useRef(false);

  const shouldCenterAlign = filteredTabs.length <= 3;

  const handleTabClick = (tab: Tab) => {
    setActiveTab(tab.id);
    isClickScroll.current = true;

    if (brandName && modelName) {
      trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: pageType,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_mm_header",
          selected_value: tab.label,
          vehicle_make: brandName,
          vehicle_model: modelName,
          city_name: cityName,
          car_availability: carAvailability,
        },
      });
    }
   requestAnimationFrame(() => {
      const clickedTabButton = document.querySelector(
        `[data-tab-id="${tab.id}"]`
      ) as HTMLElement;
      const element = document.getElementById(tab.id);

      // Perform all reads first
      const elementRect = element?.getBoundingClientRect();

      // Then perform writes
      if (clickedTabButton) {
        clickedTabButton.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }

      if (element && elementRect) {
        const yOffset = -STICKY_HEADER_HEIGHT;
        const y = elementRect.top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    });

    setTimeout(() => {
      isClickScroll.current = false;
    }, 1000);
  };

  useEffect(() => {
    let rafId: number | null = null;

    const handleScroll = () => {
      if (isClickScroll.current) return;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        let currentSection: string | null = null;
        for (const tab of filteredTabs) {
          const section = document.getElementById(tab.id);
          if (section) {
            const rect = section.getBoundingClientRect();
            if (rect.top - STICKY_HEADER_HEIGHT <= 0) {
              currentSection = tab.id;
            }
          }
        }
        setActiveTab(currentSection);
        rafId = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    }
  }, [filteredTabs]);

  return (
    <div
      className={`${styles.tabNav} ${
        shouldCenterAlign ? styles.centerAligned : ""
      }`}
      role="navigation"
    >
      {filteredTabs.map((tab) => (
        <Button
          size="ds"
          key={tab.id}
          className={`${styles.tabButton} ${
            activeTab === tab.id ? styles.active : ""
          } ${tab.id === "chat" ? styles.chatTab : ""}`}
          onClick={() => handleTabClick(tab)}
          role="tab"
          aria-selected={activeTab === tab.id}
          data-tab-id={tab.id}
        >
          <div className={`${styles.tabWrapper} ${styles.chatTabWrapper}`}>
            {tab.id === "chat" && (
              <Image
                src={`${assetLoader()}/icons/tab/chatIcon.svg`}
                alt="Chat Icon"
                width={18}
                height={18}
                className={styles.chatIcon}
                priority={true}
              />
            )}
            {tab.label}
          </div>
        </Button>
      ))}
    </div>
  );
};

export default MMTab;
